import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Severty, ShowToast } from "../helper/toast";
import useRequest from "../hooks/useRequest";

const PLACEHOLDER_REGEX = /{{\s*[\w.]+\s*}}/g;

const extractPlaceholders = (text) => text.match(PLACEHOLDER_REGEX) || [];

const normalizeEmailHtml = (value = "") => {
  if (typeof value !== "string") return "";

  let normalized = value.trim();

  // Handle double-escaped API strings: \" and \n
  if (normalized.includes("\\n") || normalized.includes('\\"')) {
    normalized = normalized
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n")
      .replace(/\\"/g, '"');
  }

  // Remove wrapping quotes if backend returns a quoted JSON string
  if (
    normalized.startsWith('"') &&
    normalized.endsWith('"') &&
    normalized.length > 1
  ) {
    normalized = normalized.slice(1, -1);
  }

  return normalized;
};

const isFullHtmlDocument = (value = "") => /<html[\s>]/i.test(value) || /<!doctype/i.test(value);

const buildEmailPreviewHtml = (subject, body) => {
  const normalizedBody = normalizeEmailHtml(body);
  if (!normalizedBody) {
    return `<!doctype html><html><body style="font-family:Arial,sans-serif;padding:20px;">Email body goes here.</body></html>`;
  }

  if (isFullHtmlDocument(normalizedBody)) return normalizedBody;

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${subject || "Email Preview"}</title>
  </head>
  <body>
    ${normalizedBody}
  </body>
</html>`;
};

const serializeHtmlDocument = (doc) => {
  if (!doc?.documentElement) return "";
  const doctype = doc.doctype
    ? `<!DOCTYPE ${doc.doctype.name}${doc.doctype.publicId ? ` PUBLIC "${doc.doctype.publicId}"` : ""
    }${doc.doctype.systemId ? ` "${doc.doctype.systemId}"` : ""}>`
    : "<!DOCTYPE html>";

  return `${doctype}\n${doc.documentElement.outerHTML}`;
};

const TemplateEditor = ({ template, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const { request } = useRequest();
  const [subject, setSubject] = useState("");
  const [richBody, setRichBody] = useState("");
  const iframeRef = useRef(null);
  const [originalPlaceholders, setOriginalPlaceholders] = useState({
    subject: [],
    body: [],
  });

  useEffect(() => {
    const normalizedBody = normalizeEmailHtml(template.body_html);

    setSubject(template.subject);
    setRichBody(normalizedBody);

    setOriginalPlaceholders({
      subject: extractPlaceholders(template.subject),
      body: extractPlaceholders(normalizedBody),
    });
  }, [template]);

  const validatePlaceholders = (currentBody) => {
    const currentSubject = extractPlaceholders(subject);
    const currentBodyPlaceholders = extractPlaceholders(currentBody);

    const subjectValid = originalPlaceholders.subject.every((p) =>
      currentSubject.includes(p)
    );

    const bodyValid = originalPlaceholders.body.every((p) => currentBodyPlaceholders.includes(p));

    return subjectValid && bodyValid;
  };

  const handleSave = async () => {
    const iframeDoc = iframeRef.current?.contentDocument;
    const latestPreviewHtml = iframeDoc ? serializeHtmlDocument(iframeDoc) : richBody;
    const normalizedBody = normalizeEmailHtml(latestPreviewHtml);
    if (!validatePlaceholders(normalizedBody)) {
      ShowToast(
        "You cannot remove or change placeholders like {{user_name}}",
        Severty.ERROR
      );
      return;
    }
    setLoading(true);
    const payload = {
      subject,
      body_html: normalizedBody,
      is_active: template.is_active,
    };
    request({
      url: `/api/admin/email-template/${template.id}`,
      method: "PUT",
      data: payload,
      onSuccess: (data) => {
        setLoading(false);
        ShowToast("Template updated successfully", Severty.SUCCESS);
        onUpdate();
      },
      onError: (error) => {
        setLoading(false);
        ShowToast("Failed to update email template", Severty.ERROR);
      },
    });
  };

  const normalizedRichBody = useMemo(() => normalizeEmailHtml(richBody), [richBody]);
  const previewHtml = useMemo(() => {
    return buildEmailPreviewHtml(subject, normalizedRichBody);
  }, [subject, normalizedRichBody]);

  const handlePreviewLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc?.body) return;

    doc.body.setAttribute("contenteditable", "true");
    doc.body.style.outline = "none";
    doc.body.style.caretColor = "#1f2937";
    doc.body.style.cursor = "text";
    doc.body.setAttribute("spellcheck", "false");
  }, []);

  return (
    <div className="template-editor-wrapper">
      <h3>Edit Template</h3>

      <div className="form-group">
        <label>
          <strong >Subject</strong>
        </label>
        <input
          className="editor-subject-input form-control"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Email subject"
        />
      </div>


      <div className="email-preview-section">
        <h4>Email Preview</h4>
        <p className="email-preview-note">
          Click inside the preview and edit text directly. Use Save Changes to persist updates.
        </p>
        <div className="email-preview-frame-wrap">
          <iframe
            ref={iframeRef}
            title="Email template preview"
            className="email-preview-frame"
            srcDoc={previewHtml}
            onLoad={handlePreviewLoad}
          />
        </div>
      </div>

      <div className="placeholders-section">
        <h4>Available Placeholders</h4>
        <div className="placeholder-info">
          <p>
            You can use these placeholders in your template. They will be
            replaced with actual values when the email is sent.
          </p>
        </div>
        <div className="placeholder-groups">
          <div className="placeholder-group">
            <h5>Subject Placeholders</h5>
            <div className="placeholder-list">
              {originalPlaceholders.subject.map((p, index) => (
                <span
                  key={index}
                  className="placeholder-chip badge bg-secondary me-2 mb-2"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>

          <div className="placeholder-group">
            <h5>Body Placeholders</h5>
            <div className="placeholder-list">
              {originalPlaceholders.body.map((p, index) => (
                <span
                  key={index}
                  className="placeholder-chip badge bg-secondary me-2 mb-2"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="editor-actions mt-3">
        <button
          onClick={handleSave}
          disabled={loading}
          className="btn btn-primary"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>

    </div>
  );
};

export default TemplateEditor;
